import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { PosterField } from './PosterField';

/** What an event form hands to its page on Save: the raw input values (the page converts the times). */
export interface EventFormValues {
  title: string;
  start: string;
  /** Empty = only the start is shown. */
  end: string;
  description: string;
  /** Empty = keep the poster. */
  posterPath?: string;
  entrancePriceLabel: string;
  offersTickets: boolean;
  /** Always false on a blueprint's default event (no Featured switch there). */
  featured: boolean;
}

/**
 * One event form, shared by the night page (`datetime-local`, ids `admin.nights.event.{i}.*`) and the
 * blueprint page (`time`, ids `admin.blueprints.event.{i}.*`). Fields: `title`, `startsAt`, `endsAt`,
 * `description`, `entrancePrice`, `offersTickets` (disabled, with `offersTicketsHint` under it, while the page's Require ticket is off) and, on a night's event only, `featured` (the ids sit on the switches' <label>), `save`, `delete` and `error`
 * (the red line, present only after a refused save). `index` counts the forms in on-screen order (saved first, then unsaved).
 */
export function EventFormRow({
  idPrefix,
  index,
  timeInput,
  deleteNoun,
  withFeatured = false,
  ticketsAllowed,
  ticketsNoun,
  event,
  defaultStart,
  onSave,
  onDelete,
}: {
  idPrefix: string;
  index: number;
  timeInput: 'datetime-local' | 'time';
  /** "event" or "default event", for the delete confirmation. */
  deleteNoun: string;
  /** The night's event form shows the Featured switch; a blueprint's default event has none. */
  withFeatured?: boolean;
  /**
   * The page's live Require ticket state (unsaved toggle included). While false the Offers tickets switch is off and
   * disabled and Save sends the row's saved `offersTickets` (false for an unsaved row), so an unsaved toggle takes nothing
   * off sale; Save night is what clears it.
   */
  ticketsAllowed: boolean;
  /** "night" or "blueprint", for the hint under the disabled switch. */
  ticketsNoun: string;
  /** Absent on an unsaved (Add event) form; times are input values. */
  event?: {
    title: string;
    start: string;
    end: string;
    description?: string | null;
    posterUrl?: string | null;
    entrancePriceLabel?: string | null;
    offersTickets: boolean;
    featured?: boolean;
  };
  /** An unsaved form starts here (an input value). */
  defaultStart: string;
  onSave: (values: EventFormValues) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const id = (field: string) => `${idPrefix}.${index}.${field}`;
  const [title, setTitle] = useState(event?.title ?? '');
  const [start, setStart] = useState(event?.start ?? defaultStart);
  const [end, setEnd] = useState(event?.end ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [posterPath, setPosterPath] = useState('');
  const [entrancePriceLabel, setEntrancePriceLabel] = useState(event?.entrancePriceLabel ?? '');
  const [offersTickets, setOffersTickets] = useState(event?.offersTickets ?? false);
  // The row follows the saved flag when it changes (a night save with Require ticket off clears it on the events) without
  // dropping the row's other unsaved edits: only this switch is re-seeded.
  const [seenOffersTickets, setSeenOffersTickets] = useState(event?.offersTickets);
  if (event?.offersTickets !== seenOffersTickets) {
    setSeenOffersTickets(event?.offersTickets);
    setOffersTickets(event?.offersTickets ?? false);
  }
  const [featured, setFeatured] = useState(event?.featured ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
      <div className="grid gap-3 md:grid-cols-3">
        <FormField label="Title" required>
          <Input id={id('title')} value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormField>
        <FormField label="Starts at" required>
          <Input
            id={id('startsAt')}
            type={timeInput}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </FormField>
        <FormField label="Ends at" description="Empty shows only the start.">
          <Input
            id={id('endsAt')}
            type={timeInput}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Description">
        <textarea
          id={id('description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        />
      </FormField>
      <PosterField
        posterUrl={event?.posterUrl}
        posterPath={posterPath}
        onPathChange={setPosterPath}
      />
      <FormField
        label="Entrance price"
        description="Shown on the website when tickets are not sold online. Example: Free till 12, after €5. Leave empty to hide."
      >
        <textarea
          id={id('entrancePrice')}
          value={entrancePriceLabel}
          onChange={(e) => setEntrancePriceLabel(e.target.value)}
          rows={2}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        />
      </FormField>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4">
          <Switch
            id={id('offersTickets')}
            label="Offers tickets"
            checked={ticketsAllowed && offersTickets}
            disabled={!ticketsAllowed}
            onChange={(e) => setOffersTickets(e.target.checked)}
          />
          {withFeatured ? (
            <Switch
              id={id('featured')}
              label="Featured"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button
            id={id('delete')}
            type="button"
            size="sm"
            variant="outline"
            disabled={saving}
            onClick={async () => {
              if (event && !window.confirm(`Delete ${deleteNoun} “${event.title}”?`)) return;
              setError(null);
              try {
                await onDelete();
              } catch (err) {
                setError(graphqlErrorMessage(err, 'Could not delete this event.'));
              }
            }}
          >
            Delete
          </Button>
          <Button
            id={id('save')}
            type="button"
            size="sm"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              setError(null);
              try {
                if (!title.trim() || !start) {
                  setError('Title and Starts at are required.');
                  return;
                }
                await onSave({
                  title: title.trim(),
                  start,
                  end,
                  description: description.trim(),
                  posterPath: posterPath.trim() || undefined,
                  entrancePriceLabel: entrancePriceLabel.trim(),
                  // Not allowed (Require ticket is off in the form, maybe unsaved): send the saved value, so an abandoned
                  // toggle cannot take a ticket event off sale; Save night is what clears it.
                  offersTickets: ticketsAllowed ? offersTickets : (event?.offersTickets ?? false),
                  featured: withFeatured && featured,
                });
              } catch (err) {
                setError(graphqlErrorMessage(err, 'Could not save this event.'));
              } finally {
                setSaving(false);
              }
            }}
          >
            Save event
          </Button>
        </div>
      </div>
      {!ticketsAllowed ? (
        <p id={id('offersTicketsHint')} className="text-sm text-gray-500 dark:text-gray-400">
          Offers tickets needs Require ticket on this {ticketsNoun}.
        </p>
      ) : null}
      {error ? (
        <p id={id('error')} className="text-right text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  );
}
